import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  stockConsultationService,
  type UnifiedSearchRow,
} from "~/services/wms/stockConsultationService";
import type {
  LotTraceabilityRow,
  PalletMovementHistoryRow,
  PalletSearchResult,
  PositionStatusResult,
  ProductInventoryDetail,
  ProductSearchResult,
  StockSummary,
} from "~/types/wms";

// Type for unified search results
export type UnifiedSearchResultType =
  | "product"
  | "pallet"
  | "lot"
  | "position"
  | "oil"
  | "raw_material";

export interface UnifiedSearchResult {
  id: string;
  type: UnifiedSearchResultType;
  name: string;
  code: string | null;
  subtitle: string;
  originalData: unknown;
}

// Query keys for cache management
const STOCK_QUERY_KEYS = {
  productSearch: (search: string) => ["stock", "product-search", search],
  stockSummary: (productId: string) => ["stock", "summary", productId],
  inventoryByProduct: (productId: string) => ["stock", "inventory", productId],
  palletSearch: (barcode: string) => ["stock", "pallet-search", barcode],
  palletHistory: (palletId: string) => ["stock", "pallet-history", palletId],
  lotTraceability: (lotNumber: string) => ["stock", "lot", lotNumber],
  positionStatus: (positionId: string) => ["stock", "position", positionId],
  positionSearch: (search: string) => ["stock", "position-search", search],
};

/**
 * Hook to search products by SKU, name, barcode
 * Used for SKU query tab autocomplete
 */
export function useProductSearch(search: string) {
  return useQuery<ProductSearchResult[], Error>({
    queryKey: STOCK_QUERY_KEYS.productSearch(search),
    queryFn: () => stockConsultationService.searchProducts(search),
    enabled: search.length >= 2,
    staleTime: 30000, // 30 seconds
    // Keep the prior results on screen while the next query is in flight so the
    // list never blanks between keystrokes (an empty list mid-click gets
    // dismissed as an outside-click in modal-dialog popovers).
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to get stock summary for a product
 * Returns available, reserved, blocked quantities
 */
export function useStockSummary(productId: string | null) {
  return useQuery<StockSummary | null, Error>({
    queryKey: STOCK_QUERY_KEYS.stockSummary(productId || ""),
    queryFn: () => stockConsultationService.getStockSummary(productId!),
    enabled: !!productId,
  });
}

/**
 * Hook to get inventory details by product (positions with inventory)
 */
export function useInventoryByProduct(productId: string | null) {
  return useQuery<ProductInventoryDetail[], Error>({
    queryKey: STOCK_QUERY_KEYS.inventoryByProduct(productId || ""),
    queryFn: () => stockConsultationService.getInventoryByProduct(productId!),
    enabled: !!productId,
  });
}

/**
 * Hook to search pallets by barcode
 */
export function usePalletSearch(barcode: string) {
  return useQuery<PalletSearchResult[], Error>({
    queryKey: STOCK_QUERY_KEYS.palletSearch(barcode),
    queryFn: () => stockConsultationService.searchPalletByBarcode(barcode),
    enabled: barcode.length >= 3,
    staleTime: 30000,
  });
}

/**
 * Hook to get pallet movement history
 */
export function usePalletMovementHistory(palletId: string | null) {
  return useQuery<PalletMovementHistoryRow[], Error>({
    queryKey: STOCK_QUERY_KEYS.palletHistory(palletId || ""),
    queryFn: () => stockConsultationService.getPalletMovementHistory(palletId!),
    enabled: !!palletId,
  });
}

/**
 * Hook to get lot traceability
 */
export function useLotTraceability(lotNumber: string) {
  return useQuery<LotTraceabilityRow[], Error>({
    queryKey: STOCK_QUERY_KEYS.lotTraceability(lotNumber),
    queryFn: () => stockConsultationService.getLotTraceability(lotNumber),
    enabled: lotNumber.length >= 3,
  });
}

/**
 * Hook to get position status with content
 * Returns array of pallets at the position (one row per pallet with inventory)
 */
export function usePositionStatus(positionId: string | null) {
  return useQuery<PositionStatusResult[], Error>({
    queryKey: STOCK_QUERY_KEYS.positionStatus(positionId || ""),
    queryFn: () => stockConsultationService.getPositionStatus(positionId!),
    enabled: !!positionId,
  });
}

/**
 * Hook to search positions by code or barcode
 */
export function usePositionSearch(search: string) {
  return useQuery<
    { id: string; full_code: string; barcode: string | null }[],
    Error
  >({
    queryKey: STOCK_QUERY_KEYS.positionSearch(search),
    queryFn: () => stockConsultationService.searchPositions(search),
    enabled: search.length >= 2,
    staleTime: 30000,
  });
}

/**
 * Hook for unified stock search across all data types
 * Uses single database function to search products, pallets, lots, positions, oils, and raw materials
 */
export function useUnifiedStockSearch(search: string) {
  const enabled = search.length >= 2;

  const { data, isLoading } = useQuery<UnifiedSearchRow[], Error>({
    queryKey: ["unified-stock-search", search],
    queryFn: () => stockConsultationService.unifiedSearch(search, 10),
    enabled,
    staleTime: 30000,
  });

  // Transform database results to UnifiedSearchResult format
  const results: UnifiedSearchResult[] = (data || []).map((row) => ({
    id: row.id,
    type: row.result_type as UnifiedSearchResultType,
    name: row.name,
    code: row.code,
    subtitle: row.subtitle,
    originalData: row.extra_data,
  }));

  return {
    results,
    isLoading: enabled && isLoading,
    isEmpty: enabled && !isLoading && results.length === 0,
  };
}
